import type { Work } from './types';

// --- Verified Unsplash IDs (curl-tested 200 OK). Keep this list in sync if you
// rotate hero photos: every URL in this file uses an ID from below.
//   photo-1501939387519-cf9c35d4f4eb  · ornamental/floral
//   photo-1542038784456-1ea8e935640e  · drawing hand / sketch
//   photo-1547754145-ef9ff306e3f3     · fine line on bicep
//   photo-1557130641-1b14718f096a     · large-scale blackwork
//   photo-1565058379802-bbe93b2f703a  · session / process shot
//   photo-1575492899586-009d962fc732  · figurative blackwork
//   photo-1593989716762-392976e5340f  · dotwork
//   photo-1610942933193-8fafd0973f6d  · magnolia (hero)
//   photo-1611501275019-9b5cda994e8d  · close-up detail
//   photo-1612911012211-d14e442e4739  · full sleeve
//   photo-1641636480285-4dfa1ba02b35  · lettering
//   photo-1651692883249-ed36b3523419  · multi fine line
//   photo-1669476526639-e390ead82f4b  · cypress / botanical
//   photo-1700652793469-6fcbffc9aed6  · ornamental chest
//   photo-1736594554949-f3cb2d0aac37  · handpoke session
//   photo-1736628153972-9850cf7fddfe  · handpoke hand
//   photo-1736628283631-8d9c8167fa88  · finger initials

const u = (id: string, w = 1600, q = 80) =>
  `https://images.unsplash.com/${id}?w=${w}&q=${q}&auto=format&fit=crop`;

const IMG = {
  magnolia: 'photo-1610942933193-8fafd0973f6d',
  statua: 'photo-1575492899586-009d962fc732',
  sleeve: 'photo-1612911012211-d14e442e4739',
  blackwide: 'photo-1557130641-1b14718f096a',
  fineBicep: 'photo-1547754145-ef9ff306e3f3',
  fineMulti: 'photo-1651692883249-ed36b3523419',
  handpokeHand: 'photo-1736628153972-9850cf7fddfe',
  handpokeSession: 'photo-1736594554949-f3cb2d0aac37',
  lettering: 'photo-1641636480285-4dfa1ba02b35',
  initial: 'photo-1736628283631-8d9c8167fa88',
  ornamental: 'photo-1700652793469-6fcbffc9aed6',
  floral: 'photo-1501939387519-cf9c35d4f4eb',
  dotwork: 'photo-1593989716762-392976e5340f',
  cypress: 'photo-1669476526639-e390ead82f4b',
  sketch: 'photo-1542038784456-1ea8e935640e',
  detail: 'photo-1611501275019-9b5cda994e8d',
  session: 'photo-1565058379802-bbe93b2f703a',
} as const;

export const works: Work[] = [
  {
    id: 'w-001',
    title: { it: 'Magnolia notturna', en: 'Night magnolia' },
    artistSlug: 'davi-marcal',
    style: 'blackwork',
    placement: { it: 'Avambraccio interno', en: 'Inner forearm' },
    size: '14 × 18 cm',
    hours: 6,
    year: 2025,
    image: u(IMG.magnolia),
    alt: {
      it: 'Magnolia in blackwork pesante sull’avambraccio',
      en: 'Heavy blackwork magnolia on the forearm',
    },
    description: {
      it: 'Composizione su due sedute. Pieni costruiti per primi, dettagli interni in seconda passata.',
      en: 'Two-session piece. Solids built first, internal details in the second pass.',
    },
    story: [
      {
        index: '01',
        stamp: { it: 'Gennaio 2025 · Consulenza', en: 'January 2025 · Consult' },
        title: { it: 'Una pianta che non chiude mai', en: 'A flower that never closes' },
        body: {
          it: 'Camilla arriva con dodici screenshot di magnolie su Pinterest e un disegno fatto da sé. Non vuole una magnolia decorativa: vuole quella di sua nonna, nel cortile a Lecce, che fioriva di notte. Davi disegna sul tavolo, non sullo schermo: tre schizzi a matita in mezz’ora, uno sopravvive.',
          en: 'Camilla shows up with twelve Pinterest screenshots and a sketch of her own. She doesn’t want a decorative magnolia — she wants her grandmother’s, in the courtyard in Lecce, that bloomed at night. Davi draws on the table, not on a screen: three pencil sketches in half an hour, one survives.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Febbraio 2025 · Bozzetto', en: 'February 2025 · Sketch' },
        title: { it: 'Pieno prima della linea', en: 'Solids before line' },
        body: {
          it: 'Davi lavora il blackwork al contrario rispetto al disegno classico: prima i pieni neri, poi la struttura. La magnolia è quasi tutta ombra, con tre petali in negativo che catturano la luce. Sull’avambraccio interno il disegno segue la flessione — ogni piega della pelle è una piega del petalo.',
          en: 'Davi works blackwork backwards: solids first, structure later. The magnolia is almost all shadow, three negative-space petals catching the light. On the inner forearm the drawing follows the bend — every fold of skin is a fold of petal.',
        },
        image: u(IMG.detail),
      },
      {
        index: '03',
        stamp: { it: 'Seduta 01 · 02/03 · 3h 40min', en: 'Session 01 · 02/03 · 3h 40min' },
        title: { it: 'I neri pieni', en: 'The solid blacks' },
        body: {
          it: 'Pavone Solid Black. Ago RL 9 per il contorno strutturale, magnum 13 per i pieni. Si lavora dall’alto verso il basso, dalla zona meno sensibile a quella più. Camilla porta un libro e non lo apre. Pausa ogni quarantacinque minuti. A fine seduta la magnolia è una sagoma piatta — quel che si vede oggi è il sotto del tatuaggio.',
          en: 'Pavone Solid Black. RL 9 for the structural outline, magnum 13 for the solids. Top down, least to most sensitive zone. Camilla brings a book and never opens it. A break every forty-five minutes. By the end the magnolia is a flat silhouette — what you see today is the underside of the tattoo.',
        },
        image: u(IMG.session),
      },
      {
        index: '04',
        stamp: { it: 'Seduta 02 · 23/03 · 2h 20min', en: 'Session 02 · 23/03 · 2h 20min' },
        title: { it: 'Quello che mangia la luce', en: 'What eats the light' },
        body: {
          it: 'Tre settimane dopo la pelle ha rilasciato. Si entra nei pieni con whip shading molto fitta — i petali in negativo respirano. Si chiude con un singolo dettaglio in rosso sangue: una sola goccia, dove cade lo sguardo. È l’unico colore del pezzo, e Davi non lo ripeterà mai.',
          en: 'Three weeks later the skin has released. Dense whip shading inside the solids — the negative petals start to breathe. We close with one detail in blood red: a single drop, where the eye lands. The only colour in the piece, and Davi won’t repeat it.',
        },
        image: u(IMG.blackwide),
      },
      {
        index: '05',
        stamp: { it: 'Sei mesi dopo · Settembre 2025', en: 'Six months later · September 2025' },
        title: { it: 'Quando guarisce', en: 'After it heals' },
        body: {
          it: 'Il nero del blackwork pesante perde fra il dodici e il quindici per cento di intensità nel primo mese, poi si stabilizza. La magnolia di Camilla, ora, è quella che voleva: scura abbastanza da sembrare un’ombra sull’avambraccio, viva abbastanza da farsi cercare. Nessun ritocco previsto.',
          en: 'Heavy blackwork loses twelve to fifteen percent of its intensity in the first month, then settles. Camilla’s magnolia, now, is the one she wanted: dark enough to look like a shadow on her forearm, alive enough to be sought out. No touch-up planned.',
        },
        image: u(IMG.magnolia),
      },
    ],
  },

  {
    id: 'w-002',
    title: { it: 'Statua VII', en: 'Statue VII' },
    artistSlug: 'davi-marcal',
    style: 'blackwork',
    placement: { it: 'Bicipite', en: 'Bicep' },
    size: '11 × 22 cm',
    hours: 8,
    year: 2024,
    image: u(IMG.statua),
    alt: { it: 'Tatuaggio figurativo blackwork sul braccio', en: 'Figurative blackwork piece on the arm' },
    story: [
      {
        index: '01',
        stamp: { it: 'Settembre 2024 · Consulenza', en: 'September 2024 · Consult' },
        title: { it: 'Marmo, non eroe', en: 'Marble, not hero' },
        body: {
          it: 'Stefano voleva una statua greca. Davi ha rifiutato la prima volta: troppe statue greche, sempre uguali. Sono tornati con una sola fotografia, di un torso senza testa al Museo Archeologico di Napoli. Solo quello. Niente nome del personaggio.',
          en: 'Stefano wanted a Greek statue. Davi turned him down once: too many Greek statues, all the same. They came back with a single photograph — a headless torso at the Naples Archaeological Museum. Just that. No character name.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Ottobre 2024 · Tre sedute · 8h', en: 'October 2024 · Three sessions · 8h' },
        title: { it: 'L’ombra prima del muscolo', en: 'Shadow before muscle' },
        body: {
          it: 'Il pezzo cresce per masse, non per dettaglio. Prima i piani d’ombra del torso, poi i contorni morbidi. Niente linea che divide muscoli — la divisione la fa il nero pieno. Tre sedute distanziate di una settimana ciascuna per non irritare la pelle del bicipite.',
          en: 'The piece grows in masses, not in detail. Shadow planes of the torso first, then the soft outlines. No line dividing muscles — the solid black divides. Three sessions a week apart so the bicep skin doesn’t get angry.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Finito · Novembre 2024', en: 'Closed · November 2024' },
        title: { it: 'Quello che resta', en: 'What remains' },
        body: {
          it: 'Niente firma, nessun anno inciso. È un pezzo che vuole sembrare antico anche da nuovo. Stefano è tornato a marzo per la prima foto pulita: si è accorto che la statua è più sua di quanto avesse pensato.',
          en: 'No signature, no engraved year. A piece that wants to look old when it’s new. Stefano came back in March for the first clean photo: he realised the statue is more his than he had thought.',
        },
        image: u(IMG.statua),
      },
    ],
  },

  {
    id: 'w-003',
    title: { it: 'Sleeve — capitolo primo', en: 'Sleeve — chapter one' },
    artistSlug: 'davi-marcal',
    style: 'blackwork',
    placement: { it: 'Manica intera', en: 'Full sleeve' },
    size: '60 cm',
    hours: 24,
    year: 2024,
    image: u(IMG.sleeve),
    alt: { it: 'Sleeve blackwork integrale', en: 'Full blackwork sleeve' },
    description: {
      it: 'Quattro sedute distribuite su sei mesi. Riferimenti: incisioni di Doré, anatomie barocche.',
      en: 'Four sessions spread over six months. References: Doré engravings, baroque anatomies.',
    },
    story: [
      {
        index: '01',
        stamp: { it: 'Aprile 2024 · Consulenza', en: 'April 2024 · Consult' },
        title: { it: 'Un libro, non un’immagine', en: 'A book, not an image' },
        body: {
          it: 'Lorenzo non porta riferimenti pronti. Porta un volume di Gustave Doré e ci appoggia sopra un foglio bianco. "Lo sleeve è la mia copertina, non il mio poster." Davi accetta a una condizione: niente decisioni rapide. Sei mesi minimo.',
          en: 'Lorenzo brings no ready-made references. He brings a volume of Gustave Doré and lays a blank sheet on top. "The sleeve is my cover, not my poster." Davi accepts on one condition: no quick decisions. Six months minimum.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Sedute 01—03 · Maggio—Luglio · 18h', en: 'Sessions 01—03 · May—July · 18h' },
        title: { it: 'Architettura prima della scena', en: 'Architecture before scene' },
        body: {
          it: 'Si parte dalla struttura: linee guida sul deltoide, primi pieni nella zona del bicipite, niente narrativa ancora. Lorenzo viene una volta al mese, sempre il giovedì. La sleeve cresce a strati come un’incisione che si rivela.',
          en: 'Structure first: guidelines on the deltoid, the first solids around the bicep, no narrative yet. Lorenzo comes once a month, always on a Thursday. The sleeve grows in layers like an engraving revealing itself.',
        },
        image: u(IMG.detail),
      },
      {
        index: '03',
        stamp: { it: 'Seduta 04 · Settembre · 6h', en: 'Session 04 · September · 6h' },
        title: { it: 'Capitolo primo, virgola', en: 'Chapter one, comma' },
        body: {
          it: 'L’ultima seduta è un’ora di lavoro e cinque di osservazione. La sleeve è chiusa ma la storia no — Lorenzo torna l’anno prossimo per l’avambraccio. Davi non si firma sotto un pezzo che continua.',
          en: 'The last session is one hour of work and five of observation. The sleeve is closed but the story is not — Lorenzo comes back next year for the forearm. Davi doesn’t sign under a piece that continues.',
        },
        image: u(IMG.sleeve),
      },
    ],
  },

  {
    id: 'w-004',
    title: { it: 'Polmone', en: 'Lung' },
    artistSlug: 'davi-marcal',
    style: 'blackwork',
    placement: { it: 'Schiena alta', en: 'Upper back' },
    size: '32 × 28 cm',
    hours: 10,
    year: 2025,
    image: u(IMG.blackwide),
    alt: { it: 'Tatuaggio blackwork large scale', en: 'Large scale blackwork piece' },
    story: [
      {
        index: '01',
        stamp: { it: 'Febbraio 2025 · Consulenza', en: 'February 2025 · Consult' },
        title: { it: 'Dopo la malattia', en: 'After illness' },
        body: {
          it: 'Anna è guarita da un anno. Vuole un polmone, anatomico, ma "non da libro di medicina". Vuole che si veda che respira. Davi non disegna in consulenza — fa parlare lei per quaranta minuti e poi chiede se può essere asimmetrico, il polmone. Risposta: sì.',
          en: 'Anna’s been well for a year. She wants a lung, anatomical, but "not from a medical book". She wants it to look like it breathes. Davi doesn’t draw in consult — he lets her talk for forty minutes and then asks if it can be asymmetrical, the lung. Answer: yes.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta unica · 14/06 · 10h', en: 'One session · 14/06 · 10h' },
        title: { it: 'Scala uno-a-uno', en: 'Life-size' },
        body: {
          it: 'Il polmone è stampato in scala 1:1 e applicato come stencil sulla scapola. Davi lavora dieci ore in una giornata, due pause da venti minuti. Anna ascolta solo musica classica. A fine seduta il polmone è completo — nessuna correzione prevista, nessun ritocco di guarigione.',
          en: 'The lung is printed at 1:1 scale and applied as a stencil on the shoulder blade. Davi works ten hours in one day, two twenty-minute breaks. Anna only listens to classical. By the end the lung is complete — no corrections planned, no healing touch-ups.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Tre mesi dopo · Settembre 2025', en: 'Three months later · September 2025' },
        title: { it: 'Dove tira l’aria', en: 'Where the air pulls' },
        body: {
          it: 'La schiena alta tiene il nero meglio di qualsiasi zona — quasi nessuna perdita di pigmento. Il polmone di Anna sembra adesso esattamente quello che voleva: scuro, anatomico, vivo. Continua a respirare, come da contratto.',
          en: 'Upper back holds black better than almost anywhere — almost no pigment loss. Anna’s lung now looks exactly like she wanted: dark, anatomical, alive. Still breathing, as agreed.',
        },
        image: u(IMG.blackwide),
      },
    ],
  },

  {
    id: 'w-005',
    title: { it: 'Sutura', en: 'Suture' },
    artistSlug: 'cora-levy',
    style: 'fineline',
    placement: { it: 'Bicipite', en: 'Bicep' },
    size: '7 × 12 cm',
    hours: 3,
    year: 2025,
    image: u(IMG.fineBicep),
    alt: { it: 'Tatuaggio fine line sul bicipite', en: 'Fine line tattoo on bicep' },
    story: [
      {
        index: '01',
        stamp: { it: 'Marzo 2025 · Consulenza', en: 'March 2025 · Consult' },
        title: { it: 'Una linea che ricuce', en: 'A line that stitches' },
        body: {
          it: 'Giulia ha una cicatrice di otto centimetri sul bicipite. Non vuole nasconderla — vuole un fine line che la cuce, una linea che attraversa la cicatrice perpendicolare, come un segno di sutura. Cora accetta solo dopo averla visitata: la cicatrice è guarita da diciotto mesi, può sopportare l’ago.',
          en: 'Giulia has an eight-centimetre scar on her bicep. She doesn’t want to hide it — she wants a fine line stitching across it, perpendicular, like a suture mark. Cora only agrees after a physical exam: the scar has been healed eighteen months, the needle can pass.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 11/04 · 3h', en: 'Session · 11/04 · 3h' },
        title: { it: 'Sopra il tessuto', en: 'Over the tissue' },
        body: {
          it: 'Ago RL 1 a velocità ridotta. Sopra la cicatrice la pelle reagisce diversamente — Cora lavora a passate brevi, asciuga ogni dieci secondi. Giulia non guarda, parla con sua sorella in videocall. La sutura nera entra nel suo segno chiaro come un disegno geometrico che ha sempre voluto esserci.',
          en: 'RL 1 needle, low speed. Skin behaves differently over scar tissue — Cora works in short passes, wipes every ten seconds. Giulia doesn’t look, she talks to her sister on a video call. The black suture enters her pale mark like a geometric drawing that always wanted to be there.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Otto settimane dopo · Giugno 2025', en: 'Eight weeks later · June 2025' },
        title: { it: 'Non più la prima cosa', en: 'No longer the first thing' },
        body: {
          it: 'Giulia ci scrive: "Non è più la prima cosa che vedo." Era l’unico obiettivo dichiarato della consulenza. Caso chiuso.',
          en: 'Giulia writes us: "It’s no longer the first thing I see." That was the only declared goal of the consult. Case closed.',
        },
        image: u(IMG.fineBicep),
      },
    ],
  },

  {
    id: 'w-006',
    title: { it: 'Inventario', en: 'Inventory' },
    artistSlug: 'cora-levy',
    style: 'fineline',
    placement: { it: 'Avambraccio', en: 'Forearm' },
    size: '9 × 16 cm',
    hours: 4,
    year: 2025,
    image: u(IMG.fineMulti),
    alt: { it: 'Avambraccio con multipli tatuaggi fine line', en: 'Forearm with multiple fine line tattoos' },
    description: {
      it: 'Sei micro-elementi pensati come una raccolta. Linea costante 0.25mm, niente ombre.',
      en: 'Six micro-elements designed as a collection. Constant 0.25mm line, no shading.',
    },
    story: [
      {
        index: '01',
        stamp: { it: 'Maggio 2025 · Consulenza', en: 'May 2025 · Consult' },
        title: { it: 'Sei cose, non sei tatuaggi', en: 'Six things, not six tattoos' },
        body: {
          it: 'Sofia arriva con una lista: un orologio rotto, una chiave, una cassetta, una candela, una foglia, una stella. Sei micro-oggetti di un trasloco. Cora propone una regola: linea costante a 0.25mm, niente ombre, niente firme. Sei cose, una sola scrittura.',
          en: 'Sofia shows up with a list: a broken watch, a key, a cassette, a candle, a leaf, a star. Six micro-objects from a move. Cora proposes a rule: constant 0.25mm line, no shading, no signatures. Six things, one script.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 02/06 · 4h', en: 'Session · 02/06 · 4h' },
        title: { it: 'Come si stampa un catalogo', en: 'Printing a catalogue' },
        body: {
          it: 'Si lavora in ordine di dimensione: il più grande in alto, i più piccoli in basso. Ogni pezzo è uno stencil separato, posizionato a mano. Tra un oggetto e l’altro Cora aspetta dieci minuti — la pelle deve calmarsi. Alla fine non sembra un avambraccio tatuato: sembra una pagina.',
          en: 'We work largest to smallest, top to bottom. Each piece is a separate stencil, hand-placed. Cora waits ten minutes between objects — the skin must settle. By the end it doesn’t look like a tattooed forearm: it looks like a page.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Tre mesi dopo', en: 'Three months later' },
        title: { it: 'La raccolta tiene', en: 'The collection holds' },
        body: {
          it: 'Il fine line a 0.25mm tende a sbiadire più del medio — Cora prevede un ritocco a dodici mesi se servirà. Sofia, intanto, ha già preparato una seconda lista. Si vedono nel 2026.',
          en: 'Fine line at 0.25mm tends to fade faster than medium — Cora plans a touch-up at twelve months if needed. Sofia, meanwhile, has prepared a second list. See you in 2026.',
        },
        image: u(IMG.fineMulti),
      },
    ],
  },

  {
    id: 'w-007',
    title: { it: 'Mano sinistra', en: 'Left hand' },
    artistSlug: 'cora-levy',
    style: 'handpoke',
    placement: { it: 'Dorso della mano', en: 'Back of the hand' },
    size: '5 × 6 cm',
    hours: 2,
    year: 2024,
    image: u(IMG.handpokeHand),
    alt: { it: 'Tatuaggio handpoke sulla mano', en: 'Handpoke tattoo on the hand' },
    story: [
      {
        index: '01',
        stamp: { it: 'Ottobre 2024 · Consulenza', en: 'October 2024 · Consult' },
        title: { it: 'Niente macchina', en: 'No machine' },
        body: {
          it: 'Marta vuole un piccolo simbolo sulla mano. Cora suggerisce handpoke: meno aggressivo sulla pelle sottile del dorso, e visivamente più antico. La consulenza si chiude con due regole: nessuno schema dietro al simbolo, e Marta non lo dice a nessuno cosa significa.',
          en: 'Marta wants a small symbol on her hand. Cora suggests handpoke: less aggressive on the thin skin of the back of the hand, and visually more ancient. The consult ends with two rules: no scheme behind the symbol, and Marta tells no one what it means.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 18/11 · 2h', en: 'Session · 18/11 · 2h' },
        title: { it: 'Punto, pausa, punto', en: 'Dot, pause, dot' },
        body: {
          it: 'Ago a tre punte, manico di legno, inchiostro nero diluito. Cora lavora a punti singoli — circa milleottocento per il pezzo intero. Niente musica. Marta racconta una storia su sua madre per due ore. A fine seduta il simbolo è lì, scuro come carbone fresco.',
          en: 'Three-prong needle, wooden handle, diluted black ink. Cora works single dots — about eighteen hundred for the whole piece. No music. Marta tells a story about her mother for two hours. By the end the symbol is there, dark as fresh coal.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Sei mesi dopo · Maggio 2025', en: 'Six months later · May 2025' },
        title: { it: 'L’handpoke schiarisce', en: 'Handpoke lightens' },
        body: {
          it: 'L’handpoke perde più della macchina — fra il venti e il venticinque per cento in sei mesi. È atteso e voluto: il simbolo invecchia come una matita, non come un tatuaggio. Marta non vuole ritocchi. Lo dice in due righe, via email.',
          en: 'Handpoke fades more than machine — twenty to twenty-five percent in six months. It’s expected, and wanted: the symbol ages like pencil, not like a tattoo. Marta wants no touch-up. She says so in two lines by email.',
        },
        image: u(IMG.handpokeHand),
      },
    ],
  },

  {
    id: 'w-008',
    title: { it: 'Sessione', en: 'Session' },
    artistSlug: 'cora-levy',
    style: 'handpoke',
    placement: { it: 'Avambraccio', en: 'Forearm' },
    size: '10 × 14 cm',
    hours: 5,
    year: 2025,
    image: u(IMG.handpokeSession),
    alt: { it: 'Sessione di tatuaggio handpoke sull’avambraccio', en: 'Handpoke session on the forearm' },
    story: [
      {
        index: '01',
        stamp: { it: 'Aprile 2025 · Consulenza', en: 'April 2025 · Consult' },
        title: { it: 'Lentamente, per scelta', en: 'Slowly, on purpose' },
        body: {
          it: 'Pietro lavora in editoria, ha quaranta anni e nessun tatuaggio. Vuole il primo, e vuole farselo in handpoke proprio perché ci vorrà più tempo. Cora lo invita a passare un pomeriggio in studio prima di decidere — vuole che sappia cosa significa cinque ore di pulsazione.',
          en: 'Pietro works in publishing, forty years old, no tattoos. He wants his first, and he wants it handpoke precisely because it will take longer. Cora invites him to spend an afternoon in the studio before deciding — she wants him to know what five hours of pulse feel like.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta unica · 12/05 · 5h', en: 'One session · 12/05 · 5h' },
        title: { it: 'Mille linee, una alla volta', en: 'A thousand lines, one at a time' },
        body: {
          it: 'Il soggetto è semplice — un disegno geometrico ottenuto piegando un foglio quadrato. La complessità è nella manualità: ogni linea è una serie di punti vicinissimi, dosati a pressione di polso. Cinque ore senza una pausa lunga. Pietro non parla quasi mai. È l’esperienza che ha pagato.',
          en: 'The subject is simple — a geometric drawing made by folding a square sheet. Complexity lives in the hand: every line is a chain of close-set dots, paced by wrist pressure. Five hours without a long break. Pietro barely speaks. That’s the experience he paid for.',
        },
        image: u(IMG.handpokeSession),
      },
      {
        index: '03',
        stamp: { it: 'Un mese dopo', en: 'One month later' },
        title: { it: 'Il segno si è sciolto bene', en: 'The mark settled well' },
        body: {
          it: 'Cora chiede una foto di guarigione a quattro settimane. Pietro la manda alle 7:14 di mattina, senza didascalia. La linea ha mantenuto la fermezza che voleva.',
          en: 'Cora asks for a healed photo at four weeks. Pietro sends it at 7:14 AM, no caption. The line kept the firmness he wanted.',
        },
        image: u(IMG.detail),
      },
    ],
  },

  {
    id: 'w-009',
    title: { it: 'Versetto', en: 'Verse' },
    artistSlug: 'tomas-reyes',
    style: 'lettering',
    placement: { it: 'Costato', en: 'Ribcage' },
    size: '18 × 4 cm',
    hours: 3,
    year: 2024,
    image: u(IMG.lettering),
    alt: { it: 'Tatuaggio lettering espressivo', en: 'Expressive lettering tattoo' },
    description: {
      it: 'Script custom disegnato a partire da una citazione. Tre revisioni del bozzetto prima della seduta.',
      en: 'Custom script drawn from a quote. Three sketch revisions before the session.',
    },
    story: [
      {
        index: '01',
        stamp: { it: 'Luglio 2024 · Consulenza', en: 'July 2024 · Consult' },
        title: { it: 'Una frase, non un font', en: 'A sentence, not a font' },
        body: {
          it: 'Elena arriva con un verso di Anne Carson. Tomás non chiede subito di che parla — chiede in quale lingua le suona. Inglese. Allora la frase si scrive in inglese. La consulenza dura quaranta minuti e niente bozzetto: lui disegnerà a casa.',
          en: 'Elena arrives with a line from Anne Carson. Tomás doesn’t ask what it means right away — he asks in which language she hears it. English. So the line is set in English. The consult takes forty minutes, no sketch: he’ll draw at home.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Tre revisioni · Luglio—Agosto', en: 'Three revisions · July—August' },
        title: { it: 'Tre proposte, una sola scrittura', en: 'Three drafts, one hand' },
        body: {
          it: 'Tomás disegna a mano. Tre versioni: una in italico stretto, una più aperta, una "che sembra una firma". Elena sceglie la terza e cambia tre parole nella punteggiatura. È così che si firma un lettering: insieme, su carta.',
          en: 'Tomás draws by hand. Three versions: tight italic, opener, and "one that looks like a signature". Elena picks the third and changes the punctuation on three words. That’s how a lettering gets signed: together, on paper.',
        },
        image: u(IMG.detail),
      },
      {
        index: '03',
        stamp: { it: 'Seduta · 04/09 · 3h', en: 'Session · 04/09 · 3h' },
        title: { it: 'Il costato non perdona', en: 'The ribs don’t forgive' },
        body: {
          it: 'Il costato è la zona peggiore per il lettering — la pelle salta col respiro. Tomás lavora a passate brevi, chiede a Elena di non parlare nei minuti più sensibili. La frase entra dritta. A fine seduta è già più sua di quanto sia mai stata sulla pagina.',
          en: 'The ribcage is the worst zone for lettering — skin jumps with each breath. Tomás works in short passes, asks Elena to stay quiet through the toughest minutes. The sentence lands straight. By the end it’s already more hers than it ever was on the page.',
        },
        image: u(IMG.lettering),
      },
    ],
  },

  {
    id: 'w-010',
    title: { it: 'Iniziali', en: 'Initials' },
    artistSlug: 'tomas-reyes',
    style: 'lettering',
    placement: { it: 'Dito', en: 'Finger' },
    size: '2 × 1 cm',
    hours: 1,
    year: 2025,
    image: u(IMG.initial),
    alt: { it: 'Iniziale didone sul dito', en: 'Didone initial on the finger' },
    story: [
      {
        index: '01',
        stamp: { it: 'Febbraio 2025 · Consulenza', en: 'February 2025 · Consult' },
        title: { it: 'Una "M"', en: 'One "M"' },
        body: {
          it: 'Camilla vuole una lettera, una sola, sul dito anulare. Tomás disegna sei "M" diverse — tutte didone, ma con varianti su grazie, peso, asse. Camilla sceglie quella più sottile. È un lavoro da un’ora che richiede un’ora di disegno.',
          en: 'Camilla wants one letter on her ring finger. Tomás draws six different "M"s — all didone, but varying serifs, weight, axis. Camilla picks the thinnest. A one-hour job that takes an hour of drawing.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 03/03 · 1h', en: 'Session · 03/03 · 1h' },
        title: { it: 'La pelle del dito si muove', en: 'Finger skin moves' },
        body: {
          it: 'Tomás avvisa: i tatuaggi sul dito perdono dieci-quindici per cento di linea già in guarigione. Camilla accetta. Si lavora con ago RL 3, un solo passaggio per non sovraccaricare. Camilla guarda tutto e parla del lavoro di una sua amica grafica.',
          en: 'Tomás warns her: finger tattoos lose ten-fifteen percent of line right in healing. Camilla accepts. RL 3 needle, one pass only to avoid overload. Camilla watches the whole time and talks about a designer friend.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Due mesi dopo', en: 'Two months later' },
        title: { it: 'Esattamente quella', en: 'Exactly that one' },
        body: {
          it: 'La "M" si è chiusa bene. Tomás propone un ritocco a sei mesi — è prassi sul dito. Camilla si annota la data sul calendario.',
          en: 'The "M" healed clean. Tomás offers a touch-up at six months — standard on the finger. Camilla puts the date in her calendar.',
        },
        image: u(IMG.initial),
      },
    ],
  },

  {
    id: 'w-011',
    title: { it: 'Geometria toracica', en: 'Thoracic geometry' },
    artistSlug: 'mei-kobayashi',
    style: 'ornamental',
    placement: { it: 'Petto', en: 'Chest' },
    size: '24 × 22 cm',
    hours: 9,
    year: 2024,
    image: u(IMG.ornamental),
    alt: { it: 'Tatuaggio ornamentale sul petto', en: 'Ornamental tattoo on the chest' },
    story: [
      {
        index: '01',
        stamp: { it: 'Giugno 2024 · Consulenza', en: 'June 2024 · Consult' },
        title: { it: 'Sopra il cuore, non al centro', en: 'Above the heart, off-centre' },
        body: {
          it: 'Riccardo vuole un ornamentale sul petto. Mei dice subito di no all’idea di "centrato sul torace": vuole spostarlo verso il pettorale sinistro, sopra il cuore, asimmetrico. Riccardo accetta dopo aver visto come la simmetria del corpo è già rotta.',
          en: 'Riccardo wants an ornamental on his chest. Mei says no to "centred on the sternum" right away: she wants it shifted to the left pectoral, above the heart, asymmetric. Riccardo agrees once he sees how the body’s own symmetry is already broken.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Due sedute · Luglio—Agosto · 9h', en: 'Two sessions · July—August · 9h' },
        title: { it: 'Costruire una rosa di vento', en: 'Building a compass rose' },
        body: {
          it: 'Mei lavora ornamentale come architettura: prima gli assi, poi le partizioni, poi la decorazione. Niente AI, niente generatore di mandala — ogni angolo è disegnato sul corpo a mano libera. Riccardo è il modello e il foglio di carta insieme.',
          en: 'Mei treats ornamental like architecture: axes first, partitions next, ornament last. No AI, no mandala generator — every angle drawn on the body, freehand. Riccardo is both the model and the sheet of paper.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Settembre 2024 · Chiusura', en: 'September 2024 · Closed' },
        title: { it: 'Quattro millimetri a sinistra', en: 'Four millimetres left' },
        body: {
          it: 'Il pezzo finito non è perfettamente simmetrico — è spostato di quattro millimetri a sinistra rispetto al teorico centro. È esattamente quello che Mei voleva. Riccardo lo sa solo se lo guarda allo specchio in controluce.',
          en: 'The finished piece isn’t perfectly symmetric — it sits four millimetres left of theoretical centre. Exactly what Mei wanted. Riccardo only notices in a mirror, against the light.',
        },
        image: u(IMG.ornamental),
      },
    ],
  },

  {
    id: 'w-012',
    title: { it: 'Mandala asimmetrico', en: 'Asymmetric mandala' },
    artistSlug: 'mei-kobayashi',
    style: 'ornamental',
    placement: { it: 'Spalla esterna', en: 'Outer shoulder' },
    size: '18 × 18 cm',
    hours: 7,
    year: 2025,
    image: u(IMG.floral),
    alt: { it: 'Composizione ornamentale floreale', en: 'Floral ornamental composition' },
    story: [
      {
        index: '01',
        stamp: { it: 'Gennaio 2025 · Consulenza', en: 'January 2025 · Consult' },
        title: { it: 'Mandala è una parola sbagliata', en: 'Mandala is the wrong word' },
        body: {
          it: 'Federica scrive "mandala" nella mail di prenotazione. Mei la corregge in consulenza: non è un mandala, è una composizione ornamentale ispirata a mandala. Differenza sostanziale — il mandala è uno strumento spirituale, e Federica non lo è. Cambiamo nome, cambia il pezzo.',
          en: 'Federica writes "mandala" in her booking email. Mei corrects her in consult: it isn’t a mandala, it’s an ornamental composition inspired by one. Substantial difference — the mandala is a spiritual instrument, and Federica isn’t. Change the name, change the piece.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 14/02 · 7h', en: 'Session · 14/02 · 7h' },
        title: { it: 'Rotto al terzo punto', en: 'Broken at the third point' },
        body: {
          it: 'La composizione parte da sei punti cardinali ma si rompe intenzionalmente al terzo: una grande forma floreale che sfora la simmetria. Mei lavora dal centro all’esterno, sette ore, una sola pausa. Federica si addormenta per dieci minuti.',
          en: 'The composition starts on six cardinal points but breaks intentionally at the third: a large floral form spilling past the symmetry. Mei works centre-out, seven hours, one break. Federica falls asleep for ten minutes.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Quattro mesi dopo · Giugno 2025', en: 'Four months later · June 2025' },
        title: { it: 'Quasi simmetrico', en: 'Almost symmetric' },
        body: {
          it: 'Da lontano sembra simmetrico. Da vicino non lo è — è la cosa che Federica preferisce.',
          en: 'From across the room it looks symmetric. Up close it isn’t — that’s what Federica likes most.',
        },
        image: u(IMG.floral),
      },
    ],
  },

  {
    id: 'w-013',
    title: { it: 'Dotwork — la prima volta', en: 'Dotwork — first time' },
    artistSlug: 'davi-marcal',
    style: 'dotwork',
    placement: { it: 'Avambraccio', en: 'Forearm' },
    size: '12 × 14 cm',
    hours: 6,
    year: 2024,
    image: u(IMG.dotwork),
    alt: { it: 'Tatuaggio dotwork in grayscale', en: 'Dotwork tattoo in grayscale' },
    story: [
      {
        index: '01',
        stamp: { it: 'Agosto 2024 · Consulenza', en: 'August 2024 · Consult' },
        title: { it: 'Non blackwork, dotwork', en: 'Not blackwork, dotwork' },
        body: {
          it: 'Andrea voleva un blackwork con Davi. Davi gli ha proposto un dotwork — il soggetto richiedeva grigi reali, non solo pieni. Per Davi era la prima volta in questo stile in dieci anni. Lo ha detto in consulenza, senza giri. Andrea ha accettato proprio per questo.',
          en: 'Andrea wanted a blackwork with Davi. Davi proposed dotwork instead — the subject needed real greys, not just solids. For Davi it was the first dotwork piece in ten years. He said so in consult, plainly. Andrea agreed because of that.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Seduta · 22/09 · 6h', en: 'Session · 22/09 · 6h' },
        title: { it: 'Densità, non linea', en: 'Density, not line' },
        body: {
          it: 'Nessun contorno strutturale. Il pezzo nasce solo per densità di punti — più stretti dove serve scuro, più radi dove serve luce. Sei ore di concentrazione assoluta. A fine seduta Davi confessa che è il pezzo che ricorderà dell’anno.',
          en: 'No structural outline. The piece grows by dot density alone — tighter where it needs to be dark, looser where light. Six hours of pure concentration. By the end Davi admits this is the piece he’ll remember this year.',
        },
        image: u(IMG.detail),
      },
      {
        index: '03',
        stamp: { it: 'Quattro mesi dopo', en: 'Four months later' },
        title: { it: 'Il dotwork respira', en: 'Dotwork breathes' },
        body: {
          it: 'Il dotwork ben fatto perde poca densità — la grana resta visibile. Andrea torna a gennaio con una proposta per un secondo pezzo, lo stesso stile. Davi accetta. Il dotwork, adesso, è dentro il vocabolario.',
          en: 'Well-made dotwork barely loses density — the grain stays visible. Andrea returns in January with a proposal for a second piece, same style. Davi agrees. Dotwork is now part of the vocabulary.',
        },
        image: u(IMG.dotwork),
      },
    ],
  },

  {
    id: 'w-014',
    title: { it: 'Cipresso', en: 'Cypress' },
    artistSlug: 'mei-kobayashi',
    style: 'blackwork',
    placement: { it: 'Polpaccio', en: 'Calf' },
    size: '11 × 30 cm',
    hours: 8,
    year: 2025,
    image: u(IMG.cypress),
    alt: { it: 'Tatuaggio botanico blackwork sul polpaccio', en: 'Botanical blackwork tattoo on the calf' },
    story: [
      {
        index: '01',
        stamp: { it: 'Marzo 2025 · Consulenza', en: 'March 2025 · Consult' },
        title: { it: 'Verticale come la zona', en: 'Vertical like the zone' },
        body: {
          it: 'Tommaso vuole un cipresso. Mei chiede solo dove — perché un cipresso, da botanico, è verticale, e solo il polpaccio ha la verticalità giusta. Si decide trenta centimetri pieni, dall’osso del ginocchio al ventre del muscolo.',
          en: 'Tommaso wants a cypress. Mei only asks where — botanically a cypress is vertical, and only the calf has the right vertical run. They settle on a full thirty centimetres, from the knee bone to the muscle belly.',
        },
        image: u(IMG.sketch),
      },
      {
        index: '02',
        stamp: { it: 'Due sedute · Aprile—Maggio · 8h', en: 'Two sessions · April—May · 8h' },
        title: { it: 'Albero come architettura', en: 'Tree as architecture' },
        body: {
          it: 'Il cipresso si costruisce come una pila di pieni neri — non rami, non foglie singole. Mei lavora il polpaccio a passate orizzontali, una linea-modulo dopo l’altra. Tommaso cammina diversamente per due giorni dopo ogni seduta. È il prezzo del polpaccio.',
          en: 'A cypress is built as a stack of black solids — no branches, no separate leaves. Mei works the calf in horizontal passes, one module-line at a time. Tommaso walks differently for two days after each session. The calf’s tax.',
        },
        image: u(IMG.session),
      },
      {
        index: '03',
        stamp: { it: 'Tre mesi dopo · Agosto 2025', en: 'Three months later · August 2025' },
        title: { it: 'L’estate l’ha confermato', en: 'Summer confirmed it' },
        body: {
          it: 'Il blackwork sul polpaccio è quello che invecchia meglio — pochissima perdita di intensità. Tommaso ha passato un’estate in pantaloncini e ha imparato che il cipresso si nota più della sua altezza.',
          en: 'Blackwork on the calf ages best — minimal density loss. Tommaso spent a summer in shorts and learned that the cypress draws more attention than his height.',
        },
        image: u(IMG.cypress),
      },
    ],
  },
];

export const getWorksByArtist = (slug: string) =>
  works.filter((work) => work.artistSlug === slug);

export const getWorkById = (id: string) => works.find((work) => work.id === id);
